import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, Download, FileWarning } from 'lucide-react';

import api from '../../services/api';
import IconButton from './IconButton';
import Tooltip from './Tooltip';
import Button from './Button';
// PARTİ 11 (FE-25) — bu dosyanın odak tuzağı tarifi `dialogA11y`ye ÇIKARILDI ve
// ATS'in 17 diyaloğuna (10 dosya) da oradan uygulandı. Burada artık kopya yok: bu bileşenin
// mevcut test süiti (DocumentViewerModal.test.js) paylaşılan kancanın gerileme
// kapısıdır.
import useDialogA11y from './dialogA11y';
import isIosWebKit from '../../utils/isIosWebKit';

// Beş özlük-dosyası yüzeyinin (özlük sekmesi, özlük detay sayfası, Belgelerim, çalışan
// belgeleri kuyruğu, işe-hazırlık belgeleri kuyruğu) ortak dosya-önizleme kabuğu. Daha önce
// hepsi `utils/openStorageFile.js` ile imzalı adresi ALIP `window.open(url, '_blank')` çağırıyordu
// — ürün sahibi bunu reddetti: belgeler ASLA yeni sekmede açılmamalı, uygulama-içi bir modalda
// (PDF için <iframe>, resim için <img>) gösterilmeli. Görsel dil ve yapı
// `pages/expenses/ExpenseDetailPage.js`in `MediaOverlay`/`MediaModal` bloğundan (o hâlâ SATIR-İÇİ,
// paylaşılmıyor) alınmıştır; mobildeki eşdeğeri `mobile/src/components/ui/MediaPreviewModal.js`dir.
//
// Sözleşme: çağıran `fileUrl` olarak KAYITLI depolama adresini verir (ör.
// `https://storage.flexbee.app/personnel/...`) — bu bileşen kendi imzalı görüntüleme URL'ini
// `/v1/storage/view` üzerinden kendisi çözer (mobildeki `getPresignedViewUrl` çağrısının web
// eşleniği). Ham adres asla bir <iframe>/<img>'e verilmez; erişim kararını sunucudaki
// FileAccessGuard verir.
//
// 2026-08-18 itibarıyla İMZALI bir adres verilmesi de güvenlidir: bazı uçlar (işe hazırlık hoş
// geldin medyası, etik ihbar ekleri) mobil uyumluluğu için imzalı adres döndürüyor ve
// `/v1/storage/view` imzayı bekçiden ÖNCE soyup kayıtlı adrese indirgiyor
// (StorageSignedUrlNormalizer). Yine de TERCİH EDİLEN girdi kayıtlı adrestir — `resolveKind`
// dosya türünü girdiden çözer ve imzalı adreste tür sorgu dizesinden önce gelir.
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.bmp', '.svg'];

// Tür, İMZALI adresten değil KAYITLI adresten çözülür. İmzalı adres (bu bileşenin kendi
// `/v1/storage/view` çağrısından döner) her zaman bir sorgu dizesi taşır (imza, son kullanma
// vb.) — ExpenseDetailPage'in `previewUrl.match(/\.pdf(\?|$)/i)` deseni tam da bunun için var:
// o bileşen zaten imzalı bir adres üzerinde çalıştığından `(\?|$)` kaçışını eklemek ZORUNDAydı.
// Burada kaynak (çağıranın verdiği depolama anahtarı/yolu) sorgu dizesi taşımaz, o yüzden aynı
// kaçışa gerek yok — yine de savunmacı olmak için olası `?`/`#` sonrası kesilir.
function resolveKind(storedAddress) {
  if (!storedAddress) return 'unknown';
  const clean = storedAddress.split(/[?#]/)[0].toLowerCase();
  if (clean.endsWith('.pdf')) return 'pdf';
  if (IMAGE_EXTENSIONS.some((ext) => clean.endsWith(ext))) return 'image';
  return 'unknown';
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.82);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalBox = styled.div`
  position: relative;
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.3);
  display: flex;
  flex-direction: column;

  /* Açılışta programatik olarak odaklanan kap budur (bkz. odak-yönetimi efekti) — gerçek
     etkileşimli elemanlar (kapat/indir) kendi odak halkalarını zaten gösterir, bu konteynerin
     tarayıcı varsayılanı dikdörtgen halkası görsel gürültüden başka bir şey katmaz. */
  &:focus {
    outline: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

const HeaderTitle = styled.span`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const Body = styled.div`
  padding: 16px;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 320px;
  min-height: 300px;

  img {
    max-width: 100%;
    max-height: 75vh;
    object-fit: contain;
    border-radius: ${({ theme }) => theme.metrics.radius.medium};
  }

  iframe {
    width: 800px;
    max-width: 100%;
    height: 75vh;
    border: none;
    border-radius: ${({ theme }) => theme.metrics.radius.medium};
  }
`;

const StatusText = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  text-align: center;
`;

const FallbackWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

// FE-37 — iOS'ta gömülü PDF önizlemesinin yerine geçen panel. Görsel dil
// `FallbackWrap` (önizlenemeyen tür) ile AYNI: kullanıcı zaten "bu belgeyi
// burada gösteremiyorum, dışarıda aç" mesajını o kalıpta tanıyor. Fark
// yalnızca açıklama satırı: burada NEDEN gösteremediğimizi söylemek zorundayız,
// çünkü aksi hâlde kullanıcı "bozuk" sanır.
const FallbackHint = styled.p`
  margin: 0;
  max-width: 360px;
  text-align: center;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

export default function DocumentViewerModal({ open, fileUrl, title, onClose }) {
  const { t } = useTranslation();
  const [signedUrl, setSignedUrl] = useState(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  // Perdeye tıklama BU kabukta kapatır (`closeOnOutsideClick: true`): önizleyici
  // kullanıcının yazdığı hiçbir veriyi taşımaz. Form diyaloglarında varsayılan
  // bunun tersidir (bkz. dialogA11y karar 1).
  // `initialFocus: 'container'` — odak kabın kendisine gider, "İndir" düğmesine
  // DEĞİL: bu bir içerik kabuğudur, form değil; ok tuşlarıyla kaydırma kabın
  // odakta olmasını gerektirir (davranış bu partiden ÖNCE de böyleydi).
  const { dialogRef, titleId, dialogProps, overlayHandlers } = useDialogA11y({
    open, onClose, closeOnOutsideClick: true, initialFocus: 'container',
  });

  // İmzalı adres HER açılışta yeniden çözülür (önbelleklenmez) — imzalı URL'ler süreli
  // olduğundan bir önceki açılıştan kalan adres artık geçersiz olabilir.
  useEffect(() => {
    if (!open || !fileUrl) return undefined;
    let cancelled = false;
    setStatus('loading');
    setSignedUrl(null);
    // api.js interceptor'ı GET okumalarında ASLA reddetmez (bkz. reference_api_interceptor_never_rejects_reads)
    // — başarısız erişimde `{status:false,data:null}` zarfı döner ve toast'ı interceptor zaten atar.
    // Bu yüzden burada `.catch()` YOK; gerçek başarı sinyali `res.data?.url`ün varlığıdır.
    api.get(`/v1/storage/view?fileUrl=${encodeURIComponent(fileUrl)}`).then((res) => {
      if (cancelled) return;
      const url = res.data?.url;
      if (url) {
        setSignedUrl(url);
        setStatus('ready');
      } else {
        setStatus('error');
      }
    });
    return () => { cancelled = true; };
  }, [open, fileUrl]);

  // Odak yönetimi (açılışta diyaloğa, kapanışta AÇAN düğmeye), ESC ve Tab
  // döngüsü artık `useDialogA11y` içinde — tarif bu dosyadan çıkarıldı.

  if (!open) return null;

  const displayTitle = title || t('documentViewer.untitled');
  const kind = resolveKind(fileUrl);
  // FE-37 — iOS/iPadOS'ta gömülü PDF SESSİZCE yalnız ilk sayfayı gösterir
  // (bkz. utils/isIosWebKit). Üç sayfalık bir sözleşmenin ilk sayfasını
  // "belgenin tamamı" sanmak, önizlemeyi hiç görmemekten DAHA KÖTÜDÜR — bu
  // yüzden orada `<iframe>` HİÇ çizilmez; yerine nedeni söyleyen ve belgeyi
  // iOS'un TAM görüntüleyicisinde açan bir panel gelir.
  //
  // Bilinçli ödün: tek sayfalık bir PDF de bu panele düşer. Sayfa sayısını
  // indirmeden bilmenin yolu yok (bir PDF ayrıştırıcı bağımlılığı eklemek bu
  // partinin işi değil) ve yanlış tarafa düşme maliyeti asimetrik: fazladan
  // bir dokunuş mu, yoksa sözleşmenin yarısını görmemek mi?
  const pdfPreviewBlocked = kind === 'pdf' && isIosWebKit();

  return (
    <Overlay data-testid="document-viewer-overlay" {...overlayHandlers}>
      <ModalBox ref={dialogRef} {...dialogProps}>
        <Header>
          <HeaderTitle id={titleId} title={displayTitle}>{displayTitle}</HeaderTitle>
          <HeaderActions>
            {signedUrl ? (
              <Tooltip text={t('documentViewer.download')}>
                <IconButton
                  as="a"
                  href={signedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('documentViewer.download')}
                >
                  <Download size={16} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip text={t('documentViewer.download')}>
                <IconButton disabled aria-label={t('documentViewer.download')}>
                  <Download size={16} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip text={t('common.close')}>
              <IconButton onClick={onClose} aria-label={t('common.close')}>
                <X size={16} />
              </IconButton>
            </Tooltip>
          </HeaderActions>
        </Header>
        <Body>
          {status === 'loading' && <StatusText>{t('common.loading')}</StatusText>}
          {status === 'error' && <StatusText>{t('documentViewer.loadError')}</StatusText>}
          {status === 'ready' && kind === 'pdf' && !pdfPreviewBlocked && (
            <iframe src={signedUrl} title={displayTitle} />
          )}
          {status === 'ready' && pdfPreviewBlocked && (
            <FallbackWrap data-testid="document-viewer-ios-pdf-fallback">
              <FileWarning size={32} />
              <FallbackHint>{t('documentViewer.iosPdfNotice')}</FallbackHint>
              <Button as="a" href={signedUrl} target="_blank" rel="noopener noreferrer">
                <Download size={16} />
                {t('documentViewer.openFullDocument')}
              </Button>
            </FallbackWrap>
          )}
          {status === 'ready' && kind === 'image' && (
            <img src={signedUrl} alt={displayTitle} />
          )}
          {status === 'ready' && kind === 'unknown' && (
            <FallbackWrap>
              <FileWarning size={32} />
              <StatusText>{t('documentViewer.cannotPreview')}</StatusText>
              <Button as="a" href={signedUrl} target="_blank" rel="noopener noreferrer">
                <Download size={16} />
                {t('documentViewer.download')}
              </Button>
            </FallbackWrap>
          )}
        </Body>
      </ModalBox>
    </Overlay>
  );
}
